import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Plus } from "react-feather";
import { getApiBase } from "../../helpers/apiBase";
import { isLocationSidebarContext } from "../../helpers/locationContext";

/**
 * Переключатель проектов в сайдбаре.
 *
 * Максим 22.08.2026: в боковом меню должен быть список «Мои проекты» с
 * переключением, а у выбранного — квадратное превью, чтобы человек понимал,
 * какой мир он сейчас настраивает.
 *
 * Это важнее, чем кажется: разделы ниже (локация, квесты, награды) относятся
 * к выбранному проекту, и без явного указания, к какому именно, человек
 * правит не тот мир и замечает это не сразу.
 *
 * Проекты берутся из /v1/cpai/location-list — там локации, где человек
 * состоит. На платформе «проект» и «локация» пока одна сущность; если они
 * разойдутся, поменяется только источник списка.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const ProjectSwitcher = () => {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const currentId = useMemo(() => {
    const raw = new URLSearchParams(search).get("location_id");
    const id = raw ? parseInt(raw, 10) : NaN;
    return Number.isNaN(id) ? null : id;
  }, [search]);

  const inLocation = isLocationSidebarContext(pathname, search);

  useEffect(() => {
    let alive = true;
    const base = getApiBase();
    if (!base) return undefined;

    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;

    fetch(`${base}/v1/cpai/location-list`, {
      method: "POST",
      headers,
      body: JSON.stringify({}),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (alive && Array.isArray(data)) setProjects(data);
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  const current = projects.find((p) => p.id === currentId) || null;

  const switchTo = (id) => {
    setOpen(false);
    navigate(`${PUBLIC}/location/dashboard?location_id=${id}`);
  };

  // Вне проекта переключать нечего — показываем вход в список миров.
  if (!inLocation) {
    return (
      <div className="fw-switcher">
        <Link className="fw-switcher-empty" to={`${PUBLIC}/location-list`}>
          <span className="fw-switcher-pic fw-switcher-pic--blank">+</span>
          <span className="fw-switcher-text">
            <b>Мои проекты</b>
            <small>{projects.length ? `${projects.length} в работе` : "создать первый"}</small>
          </span>
        </Link>
      </div>
    );
  }

  return (
    <div className="fw-switcher">
      <button type="button" className="fw-switcher-current" onClick={() => setOpen(!open)}>
        <span className="fw-switcher-pic">
          {current && (current.image32 || current.image) ? (
            <img src={current.image32 || current.image} alt="" />
          ) : (
            (current?.name || "?").slice(0, 1).toUpperCase()
          )}
        </span>
        <span className="fw-switcher-text">
          <small>Настраиваю мир</small>
          <b>{current?.name || "Проект"}</b>
        </span>
        <ChevronDown className={open ? "rot" : ""} />
      </button>

      {open && (
        <div className="fw-switcher-list">
          {projects.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fw-switcher-item${p.id === currentId ? " current" : ""}`}
              onClick={() => switchTo(p.id)}
            >
              <span className="fw-switcher-pic sm">
                {p.image32 || p.image ? (
                  <img src={p.image32 || p.image} alt="" />
                ) : (
                  (p.name || "?").slice(0, 1).toUpperCase()
                )}
              </span>
              {p.name}
            </button>
          ))}

          <Link className="fw-switcher-item fw-switcher-add" to={`${PUBLIC}/location-list/add`}>
            <span className="fw-switcher-pic sm fw-switcher-pic--blank">
              <Plus />
            </span>
            Создать проект
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;
